import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { MultiSelect } from '../components/ui/multi-select';
import { 
  Settings, 
  Search, 
  Edit, 
  Plus, 
  Loader2,
  Globe,
  BarChart,
  Shield,
  Save,
  Eye,
  Trash2,
  Monitor,
  Code,
  Copy,
  Check,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plug
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL, API_BASE_URL, isProduction, isStaging } from '../config/api';
import IntegrationStatusModal from '../components/IntegrationStatusModal';
import AddSiteDialog from '../components/AddSiteDialog';
import { SectorAssignmentModal } from '../components/site/SectorAssignmentModal';

interface Site {
  id: string;
  _id?: string; // Keep for backward compatibility
  url: string;
  name: string;
  status: 'active' | 'pending' | 'wanabee' | 'non-existing';
  site_type?: 'discovery' | 'paid' | 'trial' | 'demo';
  owner_email?: string;
  analytics: boolean;
  chatbot: boolean;
  voicebot?: boolean;
  suggestions?: boolean;
  benchmarking?: boolean;
  counter?: boolean;
  created: string;
  updated?: string;
  // Installation tracking
  pagemind_installed?: boolean;
  pagemind_version?: string;
  pagemind_api_url?: string;
  pagemind_last_check?: string;
  // Analytics data availability
  matomo_has_data?: boolean;
  openreplay_has_data?: boolean;
  snowplow_has_data?: boolean;
  // Sectors
  sector_ids?: string[];
}

export default function Sites() {
  const navigate = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Edit dialog removed - editing is done through Site Details page
  const [deletingSite, setDeletingSite] = useState<Site | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [settingsSite, setSettingsSite] = useState<Site | null>(null);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [checkingInstallation, setCheckingInstallation] = useState<string | null>(null);
  const [checkingAllInstallations, setCheckingAllInstallations] = useState(false);
  const [integrationSite, setIntegrationSite] = useState<Site | null>(null);
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isAddSiteDialogOpen, setIsAddSiteDialogOpen] = useState(false);
  const [sectors, setSectors] = useState<any[]>([]);
  const [updatingSectors, setUpdatingSectors] = useState<string | null>(null);
  const [sectorAssignmentSite, setSectorAssignmentSite] = useState<Site | null>(null);
  const [isSectorModalOpen, setIsSectorModalOpen] = useState(false);

  // Fetch sites
  const fetchSites = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/sites`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
      });
      
      if (response.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch sites');
      }
      
      const data = await response.json();
      setSites(Array.isArray(data) ? data : data.sites || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sites',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
    fetchSectors();
  }, []);

  // Fetch sectors
  const fetchSectors = async () => {
    try {
      const response = await fetch(`${API_URL}/sectors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setSectors(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching sectors:', error);
    }
  };

  // Update site sectors
  const handleSectorUpdate = async (siteId: string, sectorIds: string[]) => {
    setUpdatingSectors(siteId);
    try {
      const response = await fetch(`${API_URL}/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({ sector_ids: sectorIds }),
      });
      
      if (response.ok) {
        fetchSites();
        toast({
          title: 'Success',
          description: 'Site sectors updated successfully',
        });
      }
    } catch (error) {
      console.error('Failed to update sectors:', error);
      toast({
        title: 'Error',
        description: 'Failed to update site sectors',
        variant: 'destructive',
      });
    } finally {
      setUpdatingSectors(null);
    }
  };

  // Update site function removed - editing is done through Site Details page
  // All site updates including feature toggles should go through the dedicated
  // /sites/:siteId page which uses the /api/sites/:siteId/features endpoint

  // Delete site
  const deleteSite = async (site: Site) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL}/sites/${site.id || site._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete site');
      }

      // Remove from local state
      setSites(prev => prev.filter(s => (s.id || s._id) !== (site.id || site._id)));
      
      toast({
        title: 'Success',
        description: 'Site deleted successfully',
      });
      
      setIsDeleteDialogOpen(false);
      setDeletingSite(null);
    } catch (error) {
      console.error('Error deleting site:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete site',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter sites based on search
  const filteredSites = sites.filter(site => 
    site.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    site.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Generate JavaScript code for a site
  const generateJavaScriptCode = (site: Site) => {
    // Determine the correct apiUrl based on environment
    const apiUrl = isProduction ? 'https://api.pagemind.ai' :
                   isStaging ? 'https://api.staging.pagemind.ai' :
                   'http://localhost:3001';
    
    return `<!-- Add this before </body> tag -->
<script src="${window.location.origin}/pagemind.js"></script>
<script>
  PageMind.init({
    siteId: "${site.id || site._id}",
    apiUrl: "${apiUrl}"
  });
</script>`;
  };

  // Copy code to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(true);
      toast({
        title: 'Copied!',
        description: 'JavaScript code copied to clipboard',
      });
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      pending: 'secondary',
      wanabee: 'outline',
      'non-existing': 'destructive',
    };
    
    return (
      <Badge variant={variants[status] || 'outline'}>
        {status}
      </Badge>
    );
  };

  // Check installation for a single site
  const checkInstallation = async (siteId: string) => {
    try {
      setCheckingInstallation(siteId);
      const response = await fetch(`${API_URL}/installation/check/${siteId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (response.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to check installation');
      }

      const result = await response.json();
      
      // Update the site in the list
      setSites(prevSites => 
        prevSites.map(site => 
          (site.id || site._id) === siteId 
            ? {
                ...site,
                pagemind_installed: result.installation.installed,
                pagemind_version: result.installation.version,
                pagemind_api_url: result.installation.apiUrl,
                pagemind_last_check: result.checkedAt,
                matomo_has_data: result.analyticsData.matomo,
                openreplay_has_data: result.analyticsData.openreplay,
                snowplow_has_data: result.analyticsData.snowplow
              }
            : site
        )
      );

      toast({
        title: 'Installation checked',
        description: `PageMind ${result.installation.installed ? 'v' + result.installation.version : 'not detected'}`,
        variant: result.installation.installed ? 'default' : 'destructive',
      });
    } catch (error) {
      console.error('Error checking installation:', error);
      toast({
        title: 'Check failed',
        description: 'Failed to check installation status',
        variant: 'destructive',
      });
    } finally {
      setCheckingInstallation(null);
    }
  };

  // Check all installations
  const checkAllInstallations = async () => {
    try {
      setCheckingAllInstallations(true);
      const response = await fetch(`${API_URL}/installation/check-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start check');
      }

      toast({
        title: 'Check started',
        description: 'Checking all sites in the background...',
      });

      // Refresh the list after a delay
      setTimeout(() => {
        fetchSites();
      }, 5000);
    } catch (error) {
      console.error('Error starting bulk check:', error);
      toast({
        title: 'Check failed',
        description: 'Failed to start installation check',
        variant: 'destructive',
      });
    } finally {
      setCheckingAllInstallations(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sites Management</h1>
          <p className="text-muted-foreground">
            Manage your sites and configure their features
          </p>
        </div>
        <Button onClick={() => setIsAddSiteDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Site
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sites</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{sites.length}</div>
            <p className="text-xs text-muted-foreground">
              Across all statuses
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Analytics Enabled</CardTitle>
            <BarChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sites.filter(s => s.analytics).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Sites with analytics active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PageMind Installed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {sites.filter(s => s.pagemind_installed === true).length}
            </div>
            <p className="text-xs text-muted-foreground">
              Sites with PageMind detected
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sites Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sites</CardTitle>
          <CardDescription>
            View and manage all your sites
          </CardDescription>
          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Button
              onClick={checkAllInstallations}
              disabled={checkingAllInstallations}
              variant="outline"
              size="sm"
            >
              {checkingAllInstallations ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking All...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check All Installations
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>PageMind Status</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead>Sectors</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No sites found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSites.map((site) => (
                    <TableRow key={site.id || site._id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{site.name || 'Unnamed Site'}</div>
                          <div className="text-sm text-muted-foreground">{site.url}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={site.site_type === 'discovery' ? 'secondary' : 'default'}>
                          {site.site_type || 'paid'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(site.status)}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {site.pagemind_installed ? (
                              <Badge variant="default" className="text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Installed
                              </Badge>
                            ) : site.pagemind_installed === false ? (
                              <Badge variant="destructive" className="text-xs">
                                <XCircle className="h-3 w-3 mr-1" />
                                Not Found
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Unknown
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => checkInstallation(site.id || site._id)}
                              disabled={checkingInstallation === (site.id || site._id)}
                              title="Check installation"
                              className="h-6 w-6 p-0"
                            >
                              {checkingInstallation === (site.id || site._id) ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                          {site.pagemind_version && (
                            <div className="text-xs text-muted-foreground">
                              v{site.pagemind_version}
                            </div>
                          )}
                          {site.pagemind_last_check && (
                            <div className="text-xs text-muted-foreground">
                              Last checked: {new Date(site.pagemind_last_check).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {site.analytics && <Badge variant="outline" className="text-xs">Analytics</Badge>}
                          {site.chatbot && <Badge variant="outline" className="text-xs">Chatbot</Badge>}
                          {site.voicebot && <Badge variant="outline" className="text-xs">Voice</Badge>}
                          {site.suggestions && <Badge variant="outline" className="text-xs">Suggestions</Badge>}
                          {site.benchmarking && <Badge variant="outline" className="text-xs">Benchmark</Badge>}
                          {site.counter && <Badge variant="outline" className="text-xs">Counter</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-wrap gap-1 flex-1">
                            {site.sector_ids && site.sector_ids.length > 0 ? (
                              site.sector_ids.slice(0, 2).map(id => {
                                // Handle both integer and string IDs
                                const sector = sectors.find(s => String(s.id) === String(id));
                                return sector ? (
                                  <Badge key={String(id)} variant="outline" className="text-xs">
                                    {sector.name}
                                  </Badge>
                                ) : null;
                              })
                            ) : (
                              <span className="text-sm text-muted-foreground">No sectors assigned</span>
                            )}
                            {site.sector_ids && site.sector_ids.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{site.sector_ids.length - 2} more
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSectorAssignmentSite(site);
                              setIsSectorModalOpen(true);
                            }}
                            className="h-8 px-2"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {site.owner_email || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(site.created).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/sites/${site.id || site._id}`, { state: { from: '/admin/sites' } })}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const simulatorUrl = import.meta.env.VITE_SIMULATOR_URL || 
                                (window.location.hostname === 'admin.pagemind.ai' ? 'https://admin.pagemind.ai' : 
                                 window.location.hostname.includes('staging') ? 'https://admin.staging.pagemind.ai' : 
                                 'http://localhost:3101');
                              window.open(`${simulatorUrl}/simulator/${site.id || site._id}`, '_blank');
                            }}
                            title="Open Simulator"
                          >
                            <Monitor className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setIntegrationSite(site);
                              setIsIntegrationModalOpen(true);
                            }}
                            title="Integration Status"
                          >
                            <Plug className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSettingsSite(site);
                              setIsSettingsDialogOpen(true);
                            }}
                            title="JavaScript Settings"
                          >
                            <Code className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate(`/sites/${site.id || site._id}`, { state: { from: '/admin/sites' } });
                            }}
                            title="View/Edit Site Settings"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingSite(site);
                              setIsDeleteDialogOpen(true);
                            }}
                            className="text-destructive hover:text-destructive"
                            title="Delete Site"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this site?
            </DialogDescription>
          </DialogHeader>
          {deletingSite && (
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 rounded-lg">
                <p className="font-semibold">{deletingSite.name}</p>
                <p className="text-sm text-muted-foreground">{deletingSite.url}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Warning:</strong> This action cannot be undone. Deleting this site will:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li>Remove all site configuration and settings</li>
                  <li>Delete all pages associated with this site</li>
                  <li>Remove all topics and analysis data</li>
                  <li>Stop all monitoring and analytics</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingSite(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => deletingSite && deleteSite(deletingSite)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Site
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JavaScript Settings Dialog */}
      <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>JavaScript Integration</DialogTitle>
            <DialogDescription>
              Copy this code and add it to your website before the closing &lt;/body&gt; tag
            </DialogDescription>
          </DialogHeader>
          {settingsSite && (
            <div className="space-y-4">
              <div className="bg-gray-100 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <Label className="text-sm font-medium">JavaScript Code</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generateJavaScriptCode(settingsSite))}
                    className="h-8 px-2"
                  >
                    {copiedCode ? (
                      <>
                        <Check className="h-4 w-4 mr-1 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm">
                  <code>{generateJavaScriptCode(settingsSite)}</code>
                </pre>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Site Details</Label>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Site ID:</span>
                    <p className="font-mono">{settingsSite._id}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">URL:</span>
                    <p>{settingsSite.url}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <p>{settingsSite.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <p>{settingsSite.site_type || 'paid'}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> The apiUrl is automatically set based on your environment:
                </p>
                <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                  <li>Production: https://api.pagemind.ai</li>
                  <li>Staging: https://api.staging.pagemind.ai</li>
                  <li>Local: http://localhost:3001</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSettingsDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Integration Status Modal */}
      {integrationSite && (
        <IntegrationStatusModal
          isOpen={isIntegrationModalOpen}
          onClose={() => {
            setIsIntegrationModalOpen(false);
            setIntegrationSite(null);
          }}
          site={integrationSite}
        />
      )}

      {/* Add Site Dialog */}
      <AddSiteDialog
        isOpen={isAddSiteDialogOpen}
        onClose={() => setIsAddSiteDialogOpen(false)}
        onSiteAdded={() => {
          fetchSites();
          setIsAddSiteDialogOpen(false);
        }}
      />

      {/* Sector Assignment Modal */}
      {sectorAssignmentSite && (
        <SectorAssignmentModal
          open={isSectorModalOpen}
          onOpenChange={setIsSectorModalOpen}
          siteId={sectorAssignmentSite.id || sectorAssignmentSite._id}
          siteName={sectorAssignmentSite.name || sectorAssignmentSite.url}
          currentSectorIds={sectorAssignmentSite.sector_ids || []}
          onUpdate={(newSectorIds) => {
            // Update the site in the local state
            setSites(prev => prev.map(s =>
              (s.id || s._id) === (sectorAssignmentSite.id || sectorAssignmentSite._id)
                ? { ...s, sector_ids: newSectorIds }
                : s
            ));
            setSectorAssignmentSite(null);
          }}
        />
      )}
    </div>
  );
}